import { useMemo } from "react";
import { useThemeData } from "../../hooks/useThemeData";
import { aspectRatioEnumToNumber } from "../../helpers";
import {
  buildThemeSlideTemplateState,
  GALLERY_SLIDE_TYPES,
} from "../../buildThemeSlideTemplateState";
import type { ThemeResponse } from "../../hooks/useThemeApi";

/**
 * Derive read-only slide previews for a theme without touching the global
 * template editor store. Returns one entry per slide type the theme can render,
 * each ready to feed a SlideTemplatePreview.
 *
 * `enabled` gates the (non-trivial) per-slide state building so off-screen
 * gallery cards pay nothing until they scroll into view.
 */
export const useThemePreviewSlides = (
  theme: ThemeResponse,
  enabled = true
) => {
  const {
    logo,
    logoRatio,
    activePresenter,
    presenterRatio,
    slidesPlaceholders,
  } = useThemeData();

  // Each theme keeps its own aspect ratio. The global mediaAspectRatio is the
  // default while no theme is selected, so we resolve the ratio per theme here.
  const mediaAspectRatio = aspectRatioEnumToNumber(theme.aspect_ratio);

  return useMemo(() => {
    if (!enabled) return [];

    const commonAssets = {
      brandKitData: { logo, audio: undefined },
      initialPresenter: activePresenter,
      presenterRatio,
      logoRatio,
      forceAdjustFont: true,
    };

    return GALLERY_SLIDE_TYPES.flatMap((slideType) => {
      const slide = slidesPlaceholders.find((s) => s.slide_type === slideType);
      if (!slide) return [];
      const template = buildThemeSlideTemplateState(
        theme,
        slideType,
        slidesPlaceholders,
        mediaAspectRatio,
        commonAssets
      );
      return [{ slideType, slide, template }];
    });
  }, [
    enabled,
    theme,
    logo,
    logoRatio,
    activePresenter,
    presenterRatio,
    slidesPlaceholders,
    mediaAspectRatio,
  ]);
};
